import {Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {isDefined} from '../../../commons/commons';
import {
    AxisChartOptions,
    CACV_STRING,
    consolidationsAvailable,
    CPR_BT_STRING,
    CPR_MT_STRING,
    DataSourceInfo,
    DEFAULT_DIALOG_MAX_WIDTH,
    DEFAULT_DIALOG_MIN_WIDTH,
    getDataSourceIconInfo,
    isViewable,
    keepYourSecrets,
    mkBillingPeriodParams,
    mkEnergyRatioChart,
    mkSharedChart,
    sanityFromTimeActivationDate,
    sanityToTime,
    showNullData
} from '../../../shared/ec-economics';
import {BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {NiceValuePipe} from '../../../shared/shared.pipe';
import {BillingPeriod, BillingPeriodConfig} from '../billing-filter/ec-billing-filter.component';
import {SmagApiService} from '../../../api/smag-api.service';
import {ConfigurationService} from '../../../shared/configuration.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {I18NService} from '../../../i18n/i18n.service';
import {distinctUntilChanged, filter, first, map, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {ECMode, EnergyCommunity, EntitySubType, EntityType, PlantIncentiveMode} from 'smag-commons/cfg/hierarchy';
import {can} from '../../../shared/permissions';
import {Permission} from 'smag-commons/dto/api';
import * as moment from 'moment-timezone';
import {mkMode} from '../../../shared/energy-community';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {ECEconomicsAdvancedUserSummary, ECO_ERROR_PARAM_KEY, EconomicsErrorType} from 'smag-commons/dto/eco';
import {COSTS, EC_PLANTS_REI_REDISTRIBUTION, PLANT_TIP_QUOTE} from 'smag-commons/cfg/economics-values';
import {hasError, showError} from '../../../shared/economics-errors';
import * as _ from 'lodash';
import {ComputationStatusService} from '../../../shared/compute-status.service';

@Component({
    selector: 'app-economics-totals-community',
    templateUrl: './economics-totals-community.component.html',
    styleUrls: ['./economics-totals-community.component.scss']
})
export class EconomicsTotalsCommunityComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    private nvp: NiceValuePipe;
    private billingPeriodSubj = new ReplaySubject<BillingPeriod>(1);
    private futureEc = false;
    private timeZone: string;
    private reloadEcoGraphs: BehaviorSubject<boolean> = new BehaviorSubject(null);

    @ViewChild('ecWaitingForApproval') private ecWaitingForApprovalDialog: TemplateRef<any>;

    communityNotConfigured = false;
    hierarchyEntity: Observable<HierarchyEntity> = null;
    dataSourceInfo: DataSourceInfo;
    billingPeriodCfg: BillingPeriodConfig = null;
    billingPeriod: BillingPeriod = {} as BillingPeriod;
    inactiveEc = false;
    response: ECEconomicsAdvancedUserSummary;
    co2Title: string = null;
    gasTitle: string = null;
    energiesChartOptions: AxisChartOptions = {} as AxisChartOptions;
    energyRatioChartOptions: AxisChartOptions = {} as AxisChartOptions;
    sharedEnergyEstimatedData = true;
    sharedEnergyConsolidatedData = true;
    energyRatioEstimatedData = true;
    energyRatioConsolidatedData = true;

    readonly isViewable = isViewable;
    readonly CPR_BT_STRING = CPR_BT_STRING;
    readonly CPR_MT_STRING = CPR_MT_STRING;
    readonly EntitySubType = EntitySubType;
    readonly CACV_STRING = CACV_STRING;
    readonly EconomicsErrorType = EconomicsErrorType;
    readonly hasError = hasError;
    readonly showError = showError;
    readonly consolidationsAvailable = consolidationsAvailable;
    readonly ECMode = ECMode;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly hierarchyService: HierarchyService,
                private readonly apiService: SmagApiService,
                private readonly configurationService: ConfigurationService,
                private readonly router: Router,
                private readonly dialog: MatDialog,
                private readonly i18n: I18NService,
                private readonly computationService: ComputationStatusService) {
        this.nvp = new NiceValuePipe(i18n);
    }

    ngOnInit(): void {

        this.configurationService.timeConfig
            .pipe(first())
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);

        this.configurationService.economicsConfig.pipe(first()).subscribe(ecoCfg => {
            this.co2Title = this.i18n.translate('CO2 emission avoided by producing electricity from renewable sources.') + '\n' +
                this.i18n.translate('It can be estimated by multiplying the energy produced by the value of CO2 emitted to produce a fossil kWh of {0}.', this.nvp.transform(ecoCfg.co2_E_ef, 'gCO2/kWh')) + '\n' +
                this.i18n.translate('This coefficient also contemplates the amount of CO2 emitted during the life cycle of the PV panel.');
            this.gasTitle = this.i18n.translate('Gas not used for electricity production as it is replaced by renewable source.') + '\n' +
                this.i18n.translate('It is estimated by dividing the electricity produced by the energy contained in natural gas') +
                this.i18n.translate(' which taking into account the average efficiency of thermal power plants is {0}.', this.nvp.transform(1 / ecoCfg.gas_E_ef, 'kWh/Smc'));
        });

        // time range is stored in URL, avoid "navigation" to same entity when setting URL parameters for time range
        this.hierarchyEntity = this.hierarchyService.selection.pipe(
            distinctUntilChanged((h1, h2) => h1?.id === h2?.id),
            shareReplay(1),
            takeUntil(this.destroyed)
        );

        this.hierarchyEntity.pipe(
            filter(hierarchyEntity => !!hierarchyEntity),
            switchMap(hierarchyEntity => can(hierarchyEntity.permissions, Permission.READ) ?
                this.apiService.getEntity(hierarchyEntity.id) :
                of(null)),
            takeUntil(this.destroyed)
        ).subscribe(entity => {
            this.billingPeriodCfg = {
                pastDate: entity ? (entity as EnergyCommunity).activation_date || null : null,
                futureDate: null
            };
            if (!isDefined(this.billingPeriodCfg.pastDate)) {
                this.inactiveEc = true;
                this.showWaitingForApprovalModal();
            } else {
                this.inactiveEc = false;
                if (this.billingPeriodCfg.pastDate > moment.tz(this.timeZone).valueOf()) {
                    this.futureEc = true;
                    this.showWaitingForApprovalModal();
                } else {
                    this.futureEc = false;
                }
            }
        });

        this.hierarchyEntity.pipe(
            filter(he => he !== null && can(he.permissions, Permission.READ)),
            switchMap(he => this.apiService.communityPodsStatistics(he.id).pipe(
                map(stats => getDataSourceIconInfo(mkMode(stats))))),
            takeUntil(this.destroyed)
        ).subscribe(dataSourceIconInfo => this.dataSourceInfo = dataSourceIconInfo);

        combineLatest([this.hierarchyEntity, this.billingPeriodSubj, this.reloadEcoGraphs])
            .pipe(takeUntil(this.destroyed))
            .subscribe(([hierarchyEntity, billingPeriod, reload]) => {
                this.billingPeriod = billingPeriod;
                const toTime = sanityToTime(this.timeZone, billingPeriod.toTime);
                if (hierarchyEntity?.type === EntityType.ENERGY_COMMUNITY && this.isViewable(hierarchyEntity)) {
                    this.response = null;
                    const currentYear = moment.tz(billingPeriod.fromTime, this.timeZone).startOf('month').year();
                    this.computationService.checkComputationStatus(hierarchyEntity.id, currentYear)
                        .pipe(takeUntil(this.destroyed),
                            switchMap(() => this.apiService.ecEconomicsSummaryAdvanced(hierarchyEntity.id,
                                sanityFromTimeActivationDate(billingPeriod.fromTime, this.billingPeriodCfg.pastDate, this.timeZone), toTime)))
                        .subscribe(response => {
                            this.response = response;
                            if (!consolidationsAvailable(this.response)) {
                                this.sharedEnergyConsolidatedData = false;
                                this.energyRatioConsolidatedData = false;
                            }
                            this.energiesChartOptions = mkSharedChart(this.cfg, this.i18n, this.response,
                                this.nonIncentivizedPlants, this.sharedEnergyEstimatedData, this.sharedEnergyConsolidatedData);
                            this.energyRatioChartOptions = mkEnergyRatioChart(this.cfg, this.i18n, this.response,
                                this.nonIncentivizedPlants, this.energyRatioEstimatedData, this.energyRatioConsolidatedData);
                            this.communityNotConfigured = (this.response.errors || [])
                                .filter(err => err.type === EconomicsErrorType.MISSING_PARAM)
                                .some(err =>
                                    err.metadata[ECO_ERROR_PARAM_KEY] === COSTS ||
                                    err.metadata[ECO_ERROR_PARAM_KEY] === EC_PLANTS_REI_REDISTRIBUTION ||
                                    err.metadata[ECO_ERROR_PARAM_KEY] === PLANT_TIP_QUOTE);
                        });
                }
            });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onChangeBillingPeriod(period: BillingPeriod) {
        this.billingPeriodSubj.next(period);
        this.router.navigate([],
            {
                queryParams: mkBillingPeriodParams(period),
                queryParamsHandling: 'merge'
            }).then();
    }

    showWaitingForApprovalModal() {
        this.dialog.open(this.ecWaitingForApprovalDialog, {
            maxWidth: `${DEFAULT_DIALOG_MAX_WIDTH}px`,
            minWidth: `${DEFAULT_DIALOG_MIN_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe();
    }

    onClickMarketIndicators() {
        this.router.navigate(['/c', 'ec', 'economics', 'market'], {queryParamsHandling: 'merge'}).then();
    }

    updateEnergyConsolidated(checked: boolean) {
        this.sharedEnergyConsolidatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    updateEnergyEstimated(checked: boolean) {
        this.sharedEnergyEstimatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    updateEnergyRatioConsolidated(checked: boolean) {
        this.energyRatioConsolidatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    updateEnergyRatioEstimated(checked: boolean) {
        this.energyRatioEstimatedData = checked;
        this.reloadEcoGraphs.next(checked);
    }

    get showNullData() {
        return showNullData(this.inactiveEc, this.futureEc);
    }

    get keepYourSecrets() {
        return keepYourSecrets(this.inactiveEc, this.futureEc, this.communityNotConfigured);
    }

    get nonIncentivizedPlants() {
        return _.flatMap(this.response.pods.pods, pod => pod.plants)
            .some(plant => plant.incentive_mode === PlantIncentiveMode.NO_INCENTIVE);
    }

}
